import {
	CHAPTERS, getChapters,
	STATE_OPTIONS,
	TEN_STATE_OPTIONS
} from '../../lib/const';
import { removeHtmlTags } from '../../lib/utils';
import { ChapterPreview, type ChapterPreviewProps} from './ChapterPreview';
import megaphone from '../../assets/megaphone.png';
import moneyStack from '../../assets/money_stack.png';
import { ChapterPreviewContainer } from './ChapterPreviewContainer';
import { Box, Typography, useTheme } from '@mui/joy';
import { useConfigPage } from '../../providers/ConfigPageProvider';
import {type CSSProperties, useRef } from 'react';
import { useInView, motion } from 'framer-motion';
import { Country } from '../Country/Country';
import donate from '../../assets/collage_donate.png';
import vote from '../../assets/collage_vote_hands.png';
import {useYear} from "../../providers";

const margin = '0px 0px -500px 0px';
const previewWidth = 341;
const defaultHeaderSx: CSSProperties = {fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 'bold'}
const defaultSubHeaderSx: CSSProperties = {fontSize: '1.5rem', lineHeight: '1.625rem', fontWeight: 'normal'}

export type PreviewProps = Pick<ChapterPreviewProps, "disabled" | "disabledText" | "description" | "href" | "headerSx" | "largeCountry" | "subheaderSx"> & { width?: number };

export const StateChapterPreview = (
	{
		disabled = CHAPTERS.state.disabled,
		disabledText = CHAPTERS.state.disabledText,
		description = "",
		href,
		width = previewWidth,
		headerSx = defaultHeaderSx,
		subheaderSx = defaultSubHeaderSx,
		largeCountry = false
	} : PreviewProps
) => {
	const { isFetching, apiData } = useConfigPage();
	const { year } = useYear();
	const ref = useRef(null);
	const isInView = useInView(ref, {
		margin: margin
	});

	const chapters = getChapters(year);

	const finalDescription = apiData.data[0]?.attributes?.field_state_view_description?.processed.includes("Lorem ipsum") ? description : apiData.data[0]?.attributes?.field_state_view_description?.processed;

	return (
		<div ref={ref} style={{ display: 'flex' }}>
			<ChapterPreview
				loading={isFetching}
				splatterBg
				backgroundGradientProps={{
					sx: {
						padding: '0',
						width: width,
						pointerEvents: 'none'
					}
				}}
				chapter={chapters.state.chapter}
				countryProps={{
					available: TEN_STATE_OPTIONS.map((state) => state.id.toUpperCase()),
					includeLabels: false,
					onStateClick: (state) =>
						(location.href = `${href ?? chapters.state.url}/?selectedState=${state}`)
				}}
				description={removeHtmlTags(
					finalDescription ?? description
				)}
				disabled={disabled}
				disabledText={disabledText}
				header={chapters.state.header}
				subheader={chapters.state.subheader}
				href={href ?? chapters.state.url}
				asLink
				isActive={isInView}
				headerSx={headerSx}
				subheaderSx={subheaderSx}
				largeCountry={largeCountry}
			/>
		</div>
	);
};

export const NationalChapterPreview = (
	{
		disabled = CHAPTERS.national.disabled,
		disabledText = CHAPTERS.national.disabledText,
		description = "",
		href,
		width = previewWidth,
		headerSx = defaultHeaderSx,
		subheaderSx = defaultSubHeaderSx,
		largeCountry = false
	}: PreviewProps
) => {
	const { isFetching, apiData } = useConfigPage();
	const { year } = useYear();

	const ref = useRef(null);
	const isInView = useInView(ref, {
		margin: margin
	});

	const chapters = getChapters(year);

	const finalDescription = apiData.data[0]?.attributes?.field_national_view_description?.processed.includes("Lorem ipsum") ? description : apiData.data[0]?.attributes?.field_national_view_description?.processed;

	return (
		<div ref={ref} style={{ display: 'flex' }}>
			<ChapterPreview
				loading={isFetching}
				splatterBg
				backgroundGradientProps={{
					sx: {
						padding: '0',
						width: width,
						pointerEvents: 'none'
					}
				}}
				chapter={chapters.national.chapter}
				countryProps={{
					available: disabled
						? []
						: STATE_OPTIONS.map((state) => state.id.toUpperCase()),
					includeLabels: false
				}}
				description={removeHtmlTags(
					finalDescription ?? description
				)}
				disabled={disabled}
				disabledText={disabledText}
				header={chapters.national.header}
				subheader={chapters.national.subheader}
				href={href ?? chapters.national.url}
				asLink
				isActive={isInView}
				headerSx={headerSx}
				subheaderSx={subheaderSx}
				largeCountry={largeCountry}
			/>
		</div>
	);
};

export const DonorsChapterPreview = (
	{
		disabled = CHAPTERS.donors.disabled,
		disabledText = CHAPTERS.donors.disabledText,
		description = "",
		href,
		width = previewWidth,
		headerSx = defaultHeaderSx,
		subheaderSx = defaultSubHeaderSx,
		largeCountry = false
	}: PreviewProps
) => {
	const { isFetching, apiData } = useConfigPage();
	const theme = useTheme();
	const { year } = useYear();

	const ref = useRef(null);
	const isInView = useInView(ref, {
		margin: margin
	});

	const chapters = getChapters(year);

	const finalDescription = apiData.data[0]?.attributes?.field_donor_gaps_description?.processed.includes("Lorem ipsum") ? description : apiData.data[0]?.attributes?.field_donor_gaps_description?.processed;

	return (
		<div ref={ref} style={{ display: 'flex' }}>
			<ChapterPreviewContainer
				aria-description={`${chapters.donors.header}: ${chapters.donors.subheader}`}
				loading={isFetching}
				splatterBg
				backgroundGradientProps={{
					sx: {
						padding: '0',
						width: width,
						pointerEvents: 'none'
					}
				}}
				chapter={chapters.donors.chapter}
				disabled={disabled}
				disabledText={disabledText}
				includeLowerDots
				href={href ?? chapters.donors.url}
				asLink
				isActive={isInView}
			>
				<Box sx={{ height: '0.1px' }}></Box>
				<Box
					sx={{
						marginY: '35px',
						marginX: 'auto',
						backgroundColor: theme.palette.background.surface,
						border: '1px',
						borderStyle: 'solid',
						borderColor: '#4B5563',
						padding: '20px',
						position: 'relative',
					}}
				>
					<div style={{ visibility: 'hidden' }}>
						<Country animationDelay={0} largeCountry={largeCountry}/>
					</div>
					<motion.div
						animate={{ y: 0, opacity: 1, scaleX: 1 }}
						initial={{ y: 100, opacity: 0, scaleX: 1 }}
						transition={{ delay: 1, type: 'spring', duration: 1, bounce: 0 }}
						style={{
							position: 'relative',
							height: '100%'
						}}
					>
						<img
							src={megaphone}
							style={{
								left: largeCountry ? 12 : 0,
								bottom: largeCountry ? 0 : 9,
								position: 'absolute',
								width: largeCountry ? '60%' : '68%'
							}}
							alt='Hand holding megaphone'
						/>
						<img
							src={moneyStack}
							style={{
								transform: 'scaleX(-1)',
								left: '30%',
								bottom: largeCountry ? -30 : -36,
								position: 'absolute',
								width: largeCountry ? '45%' : '50%'
							}}
							alt='stack of money'
						/>
					</motion.div>
				</Box>
				<Typography
					level='h1'
					sx={{...headerSx}}
				>
					{chapters.donors.header}
				</Typography>
				<Typography
					level='h2'
					sx={{...subheaderSx}}
				>
					{chapters.donors.subheader}
				</Typography>
				<Typography
					sx={{
						marginTop: '9px'
					}}
				>
					{removeHtmlTags(
						finalDescription ?? description
					)}
				</Typography>
			</ChapterPreviewContainer>
		</div>
	);
};

export const AnalysisChapterPreview = (
	{
		disabled = CHAPTERS.analysis.disabled,
		disabledText = CHAPTERS.analysis.disabledText,
		description = "",
		href,
		width = previewWidth,
		headerSx = defaultHeaderSx,
		subheaderSx = defaultSubHeaderSx,
		largeCountry = false
	}: PreviewProps
) => {
	const { isFetching } = useConfigPage();
	const theme = useTheme();
	const { year } = useYear();

	const ref = useRef(null);
	const isInView = useInView(ref, {
		margin: margin
	});

	const chapters = getChapters(year);

	return (
		<div ref={ref} style={{ display: 'flex' }}>
			<ChapterPreviewContainer
				aria-description={`${chapters.analysis.header}: ${chapters.analysis.subheader}`}
				loading={isFetching}
				splatterBg
				backgroundGradientProps={{
					sx: {
						padding: '0',
						width: width,
						pointerEvents: 'none'
					}
				}}
				chapter={chapters.analysis.chapter}
				disabled={disabled}
				disabledText={disabledText}
				includeLowerDots
				href={href ?? chapters.analysis.url}
				asLink
				isActive={isInView}
			>
				<Box sx={{ height: '0.1px' }}></Box>
				<Box
					sx={{
						marginY: largeCountry ? '25px' : '35px',
						marginX: 'auto',
						backgroundColor: theme.palette.background.surface,
						border: '1px',
						borderStyle: 'solid',
						borderColor: '#4B5563',
						padding: largeCountry ? '30px 20px' : '20px',
						position: 'relative',
						transform: largeCountry ? 'scale(1.3)' : 'scale(1)'
					}}
				>
					<div style={{ visibility: 'hidden' }}>
						<Country animationDelay={0} largeCountry={largeCountry}/>
					</div>
					<motion.div
						animate={{ y: 0, opacity: 1, scaleX: 1 }}
						initial={{ y: 100, opacity: 0, scaleX: 1 }}
						transition={{ delay: 1, type: 'spring', duration: 1, bounce: 0 }}
						style={{
							position: 'relative',
							height: '100%'
						}}
					>
						<img
							src={vote}
							style={{
								left: 0,
								bottom: largeCountry ? -30 : -21,
								position: 'absolute',
								width: largeCountry ? '70%' : '93%',
							}}
							alt='Hand holding megaphone'
						/>
						<img
							src={donate}
							style={{
								left: '27%',
								bottom: -20,
								position: 'absolute',
								width: largeCountry ? '50%' : '70%',
							}}
							alt='stack of money'
						/>
					</motion.div>
				</Box>
				<Typography
					level='h1'
					sx={{...headerSx}}
				>
					{chapters.analysis.header}
				</Typography>
				<Typography
					level='h2'
					sx={{...subheaderSx}}
				>
					{`${chapters.analysis.subheader}`.replace('and', '&')}
				</Typography>
				<Typography
					sx={{
						marginTop: '9px'
					}}
				>
					{description}
				</Typography>
			</ChapterPreviewContainer>
		</div>
	);
};
